import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { USER_SETTINGS_KEYS, USER_ROLES_BY_NAME } from 'src/constants/userSetting';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { useEffect, useState } from 'react';
import { livyConfig } from 'src/lib/livyTransport';
import { livyJson } from 'src/lib/livyApi';
import { parseJwt } from 'src/core/services/jwt';

/**
 * Who may see a Livy surface, in one place.
 *
 * This is presentation only. The role is read from localStorage, which the user controls, so a
 * determined non-admin can render any of these pages. What actually stops them is the proxy, which
 * derives role and company from the verified Cognito token and refuses the request. Treat this as
 * "do not show a door that leads nowhere", never as a security boundary.
 */
const ADMIN_ROLES = [USER_ROLES_BY_NAME.LivableAdmin, USER_ROLES_BY_NAME.LivableSuperAdmin];
export const isLivyAdmin = role => ADMIN_ROLES.includes(String(role));

/** The only role offered search across every company; the proxy enforces the same rule. */
export const isLivySuperAdmin = role => String(role) === USER_ROLES_BY_NAME.LivableSuperAdmin;

/**
 * The token's role, then the settings mirror. The token belongs to whoever is signed in now: the
 * mirror is written only after getProfile, so reading it alone hid Livy until a reload right after
 * login, and the profile store is not cleared on logout, so it can still hold the last person's role.
 */
export const currentRole = () => {
  var _parseJwt, _localStorage$getItem;
  return ((_parseJwt = parseJwt((_localStorage$getItem = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '')) === null || _parseJwt === void 0 ? void 0 : _parseJwt['custom:role']) || localStorage.getItem(USER_SETTINGS_KEYS.userRole) || null;
};

/**
 * Every Livy surface is admin gated for this rollout and needs the chat endpoint configured.
 *
 * The token is checked too, not just the role. Logging out clears both, but a session that simply
 * expires leaves the role behind, and without this the launcher would sit on the login screen
 * offering an assistant that can only answer 401.
 */
export const canUseLivy = function () {
  let role = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : currentRole();
  return isLivyAdmin(role) && Boolean(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) && Boolean(livyConfig());
};

/**
 * Wraps a Livy surface so a non-admin gets one consistent answer instead of each page inventing
 * its own. Presentation only: the proxy is what actually refuses the request.
 */
export const livyUnavailableMessage = 'Livy is not available for this account yet.';

/** Customer roles a client entitlement can admit; must match CUSTOMER_ROLES in the proxy. */
const CUSTOMER_ROLES = [USER_ROLES_BY_NAME.PropertyManagerAccountHolder, USER_ROLES_BY_NAME.PropertyManagerRegular];
const signedIn = () => Boolean(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) && Boolean(livyConfig());
export const REVOKED_EVENT = 'livy:access-revoked';
const RETRY_MS = 30000;
let probe = null;

/**
 * The signed-in session: the token's subject and sign-in time, or the token itself if it carries no
 * subject. A refresh keeps auth_time, a new login changes it, so entitlement is asked again per login.
 */
export const signedInSubject = () => {
  var _localStorage$getItem2, _claims$auth_time;
  const token = (_localStorage$getItem2 = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) !== null && _localStorage$getItem2 !== void 0 ? _localStorage$getItem2 : '';
  const claims = parseJwt(token);
  return claims !== null && claims !== void 0 && claims.sub ? "".concat(claims.sub, ":").concat((_claims$auth_time = claims.auth_time) !== null && _claims$auth_time !== void 0 ? _claims$auth_time : '') : token;
};

/**
 * Whether staff have switched Livy on for this customer. Only the proxy knows (entitlement rows are
 * server side), so ask once per session and identity: /capabilities answers 200 when entitled and
 * 403 when not. Presentation only, like everything here; the proxy refuses every route regardless.
 */
export function probeCustomerAccess() {
  var _probe;
  // Keyed on the signed-in person, not their company: entitlement can list one tester and not the
  // next, and this module outlives a logout and login in the same tab.
  const key = signedInSubject();
  if (((_probe = probe) === null || _probe === void 0 ? void 0 : _probe.key) !== key) {
    const ok = livyJson('/capabilities?surface=pm').then(() => true, e => {
      // Only a refusal is an answer. A network or server failure is forgotten so the next
      // check asks again, rather than hiding Livy for the rest of the session.
      if ((e === null || e === void 0 ? void 0 : e.status) !== 403) probe = null;
      return false;
    });
    probe = {
      key,
      ok
    };
  }
  return probe.ok;
}

/** Called when a turn answers not_enabled: hide the launcher without waiting for a reload. */
export function markLivyRevoked() {
  probe = probe && _objectSpread(_objectSpread({}, probe), {}, {
    ok: Promise.resolve(false)
  });
  window.dispatchEvent(new Event(REVOKED_EVENT));
}
export const isLivyCustomer = role => CUSTOMER_ROLES.includes(String(role)) && signedIn();

/**
 * Staff as before; entitled customers once the proxy confirms. An observer passes the profile's
 * role so it re-renders when login completes. The role that decides is the token's: the profile is
 * not cleared on logout, so it can still hold the previous person's role during the next login.
 */
export function useLivyAccess(profileRole) {
  const role = currentRole();
  const staff = canUseLivy(role);
  const customer = !staff && isLivyCustomer(role);
  // The answer belongs to the person it was asked for. Keyed, so a logout and a different login in
  // the same tab never inherit it, not even for the render before the new probe returns.
  const subject = signedInSubject();
  const _useState = useState({
      subject: '',
      ok: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    entitled = _useState2[0],
    setEntitled = _useState2[1];
  useEffect(() => {
    if (!customer) return undefined;
    let live = true;
    let retry;
    const check = () => probeCustomerAccess().then(ok => {
      if (!live) return;
      setEntitled({
        subject,
        ok
      });
      // A transient failure left nothing cached, so ask again rather than stay hidden.
      if (!ok && probe === null) retry = setTimeout(check, RETRY_MS);
    });
    void check();
    window.addEventListener(REVOKED_EVENT, check);
    return () => {
      live = false;
      clearTimeout(retry);
      window.removeEventListener(REVOKED_EVENT, check);
    };
  }, [customer, subject, profileRole]);
  return staff || customer && entitled.ok && entitled.subject === subject;
}